import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { Icon } from '../../../core-components/Icon';
import { Tag } from '../../../core-components/Tag';
import { ManifestPinDialog } from '../ManifestPinDialog';

import type { I18NFromComponent, I18NInterface } from '../../../utils';
import type { FC, ReactNode } from 'react';

export type ManifestOverviewProps = {
  fallback?: boolean;

  headline?: ReactNode;
  subline?: ReactNode;
  pinDialogHeadline?: ReactNode;
  pinDialogSubline?: ReactNode;

  travellers?: ManifestTraveller[];
  i18n?: I18NFromComponent<typeof ManifestTraveller>;

  isPinRequired?: (travellerId: string) => boolean;
  onPinEnter?: (travellerId: string, pin: string) => boolean;
};

type ManifestTraveller = {
  id: string;
  mpi: number;
  status: 'incomplete' | 'complete';
  preliminary?: boolean;
  firstName?: string;
  lastName?: string;
  href?: string;
  pin?: string;
};

type ManifestTravellerProps = {
  traveller: ManifestTraveller;

  pinDialogHeadline?: ManifestOverviewProps['pinDialogHeadline'];
  pinDialogSubline?: ManifestOverviewProps['pinDialogSubline'];

  i18n?: I18NInterface<
    'statusComplete' | 'statusIncomplete' | 'statusPreliminary'
  > &
    I18NFromComponent<typeof ManifestPinDialog>;

  isPinRequired: boolean;
  onPinEnter: (pin: string) => boolean;
};

const ManifestTraveller: FC<ManifestTravellerProps> = ({
  traveller,
  pinDialogHeadline,
  pinDialogSubline,
  i18n,
  isPinRequired,
  onPinEnter,
}) => {
  const router = useRouter();
  const [showPinDialog, setShowPinDialog] = useState(false);

  const handlePinEnter = (enteredPin: string) => {
    const success = onPinEnter(enteredPin);

    // Redirect to form if pin was correct
    if (success) router.push(traveller.href ?? '#');

    return success;
  };

  const fullName =
    `${traveller.firstName ?? ''} ${traveller.lastName ?? ''}`.trim();

  if (traveller.preliminary) {
    return (
      <div
        aria-disabled
        className="flex items-center gap-4 rounded-sm bg-neutral-white p-6 text-start"
      >
        <span className="grow text-lg font-semibold text-secondary-marine-48">
          {fullName}
        </span>
        <Tag size="lg" variant="attention">
          <span className="inline-flex items-center gap-1">
            <Icon
              name="warning-circle"
              size="sm"
              className="text-attention-120"
            />
            {i18n?.statusPreliminary}
          </span>
        </Tag>
        {/* TODO: replace with "lock" icon once it lands in the component library. */}
        <Icon name="clock" size="md" className="text-attention-120" />
      </div>
    );
  }

  return (
    <>
      <Link
        href={traveller.href ?? '#'}
        className="flex items-center gap-4 rounded-sm bg-neutral-white p-6 text-start"
        onClick={e => {
          if (isPinRequired) {
            e.preventDefault();
            setShowPinDialog(true);
          }
        }}
      >
        <span className="grow text-lg font-semibold text-marine-high-emphasis">
          {fullName}
        </span>
        <Tag
          size="lg"
          variant={traveller.status === 'incomplete' ? 'default' : 'marine'}
        >
          {traveller.status === 'incomplete'
            ? i18n?.statusIncomplete
            : i18n?.statusComplete}
        </Tag>
        <Icon
          name="arrow-right"
          size="md"
          className="text-secondary-marine-100"
        />
      </Link>

      <ManifestPinDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        headline={pinDialogHeadline}
        subline={pinDialogSubline}
        travellerName={fullName}
        onPinEnter={handlePinEnter}
        i18n={i18n}
      />
    </>
  );
};

export const ManifestOverview: FC<ManifestOverviewProps> = ({
  headline,
  subline,
  pinDialogHeadline,
  pinDialogSubline,
  travellers,
  i18n,
  isPinRequired,
  onPinEnter,
}) => {
  return (
    <div className="space-y-10 text-marine-high-emphasis">
      <div className="space-y-4">
        {headline && <div className="headline-lg">{headline}</div>}
        {subline && <div className="text-lg">{subline}</div>}
      </div>

      <div className="flex flex-col gap-4">
        {travellers?.map(traveller => (
          <ManifestTraveller
            key={traveller.id}
            traveller={traveller}
            pinDialogHeadline={pinDialogHeadline}
            pinDialogSubline={pinDialogSubline}
            i18n={i18n}
            isPinRequired={isPinRequired?.(traveller.id) ?? false}
            onPinEnter={pin => onPinEnter?.(traveller.id, pin) ?? false}
          />
        ))}
      </div>
    </div>
  );
};
